import type * as React from 'react';

import { cva, type VariantProps } from 'class-variance-authority';

import { cn } from '../lib/utils';

// inset-ring, not border: Figma's stroke is drawn inside the box and takes no layout
// space, so a real border would add 2px to the height.
const alertVariants = cva(
    'relative @container w-full rounded-sm inset-ring px-4 py-3 text-base [&>svg]:size-4 [&>svg]:text-current',
    {
        variants: {
            orientation: {
                vertical:
                    'grid grid-cols-[0_1fr] items-start gap-x-3 gap-y-1 has-[>svg]:grid-cols-[calc(var(--spacing)*4)_1fr] @lg:has-[>[data-slot=alert-action]]:grid-cols-[0_1fr_auto] @lg:has-[>svg]:has-[>[data-slot=alert-action]]:grid-cols-[calc(var(--spacing)*4)_1fr_auto] [&>svg]:translate-y-0.75',
                horizontal:
                    'flex items-center gap-x-1 *:data-[slot=alert-description]:min-w-0 *:data-[slot=alert-description]:flex-1 *:data-[slot=alert-action]:mt-0 *:data-[slot=alert-action]:ml-auto [&>svg]:mr-2 [&>svg]:shrink-0',
            },
            variant: {
                base: 'bg-card text-foreground inset-ring-border',
                destructive:
                    'bg-destructive-foreground inset-ring-destructive-border text-destructive *:data-[slot=alert-description]:text-current',
                secondary:
                    'bg-secondary-background inset-ring-secondary-border text-secondary-text *:data-[slot=alert-description]:text-current',
                cyan: 'bg-alert-cyan-background inset-ring-alert-cyan-border text-alert-cyan-text *:data-[slot=alert-description]:text-current',
                warning:
                    'bg-warning inset-ring-yellow-200 text-warning-foreground *:data-[slot=alert-description]:text-current',
                green: 'bg-emerald-50 inset-ring-emerald-200 text-emerald-950 *:data-[slot=alert-description]:text-current',
                accent: 'bg-accent/50 inset-ring-0 text-foreground [&_[data-slot=alert-description]]:text-sm',
            },
        },
        defaultVariants: {
            orientation: 'vertical',
            variant: 'base',
        },
    },
);

function Alert({
    className,
    orientation,
    variant,
    ...props
}: React.ComponentProps<'div'> & VariantProps<typeof alertVariants>) {
    return (
        <div
            data-slot="alert"
            role="alert"
            className={cn(alertVariants({ orientation, variant }), className)}
            {...props}
        />
    );
}

function AlertTitle({ className, ...props }: React.ComponentProps<'div'>) {
    return (
        <div
            data-slot="alert-title"
            className={cn('col-start-2 line-clamp-1 min-h-4 font-bold tracking-tight', className)}
            {...props}
        />
    );
}

function AlertDescription({ className, ...props }: React.ComponentProps<'div'>) {
    return (
        <div
            data-slot="alert-description"
            className={cn(
                'col-start-2 grid justify-items-start gap-1 text-base text-muted-foreground [&_p]:leading-relaxed',
                className,
            )}
            {...props}
        />
    );
}

function AlertAction({ className, ...props }: React.ComponentProps<'div'>) {
    return (
        <div
            data-slot="alert-action"
            className={cn(
                'col-start-2 row-start-3 mt-2 justify-self-start @lg:col-start-3 @lg:row-span-2 @lg:row-start-1 @lg:mt-0 @lg:justify-self-auto @lg:self-center',
                className,
            )}
            {...props}
        />
    );
}

export { Alert, AlertAction, AlertDescription, AlertTitle };
